/* spell-checker: disable */

// ROLES MAP
//
// Administrator . . . . . . :Administrator
// Beholdningsstyring. . . . :Inventory
// Bogholder . . . . . . . . :Accountancy
// Byggeleder. . . . . . . . :ConstructionManager
// Entrepriseleder . . . . . :ProjectManager
// Fakturagodkender. . . . . :InvoiceApprover
// Formand . . . . . . . . . :TeamLeader
// Godkender . . . . . . . . :Approver
// Ledelse . . . . . . . . . :Management
// Montør - Opret debitor. . :FitterDebtor
// Montør - Opret sag. . . . :FitterProject
// Montør. . . . . . . . . . :Fitter
// Planlægger. . . . . . . . :Planning
// Sagsansvarlig . . . . . . :ProjectResponsible
// Supportansvarlig. . . . . :SupportResponsible
// Timegodkender . . . . . . :TimeApprover
// Timeregistrering. . . . . :HourRegistration

// The role strings should be lowercase
export enum Roles{
  Administrator = 'administrator',
  Inventory = 'inventory',
  Accountancy = 'accountancy',
  ConstructionManager = 'constructionmanager',
  ProjectManager = 'projectmanager',
  InvoiceApprover = 'invoiceapprover',
  TeamLeader = 'teamleader',
  Approver = 'approver',
  Management = 'management',
  FitterDebtor = 'fitterdebtor',
  FitterProject = 'fitterproject',
  Fitter = 'fitter',
  Planning = 'planning',
  ProjectResponsible = 'projectresponsible',
  SupportResponsible = 'supportresponsible',
  TimeApprover = 'timeapprover',
  HourRegistration = 'hourregistration'
}
/* spell-checker: enable */

export const useRoleManagerStore = defineStore('RoleManager', () => {
  const userConfigurationStore = useUserConfigurationStore();
  const accountSettingsStore = useAccountSettingsStore();
  const config = useRuntimeConfig();

  const webRoles = [
    Roles.TimeApprover,
    Roles.Administrator,
    Roles.Approver,
    Roles.Management,
    Roles.Planning,
    Roles.ProjectResponsible,
    Roles.Accountancy,
    Roles.ConstructionManager,
    Roles.ProjectManager,
    Roles.InvoiceApprover,
    Roles.SupportResponsible
  ];

  const hasMFA = computed(() => hasIntersection(userConfigurationStore.roles, webRoles));

  const showAllTabRoles = [
    Roles.ProjectResponsible,
    Roles.ProjectManager,
    Roles.ConstructionManager
  ];

  const hasShowAllTab = computed(() => hasIntersection(userConfigurationStore.roles, showAllTabRoles));

  const showResponsibleTabRoles = [
    Roles.ProjectResponsible,
    Roles.ProjectManager,
    Roles.ConstructionManager
  ];

  const hasShowResponsibleTab = computed(() => hasIntersection(userConfigurationStore.roles, showResponsibleTabRoles));

  const hasMessages = computed(() => userConfigurationStore.isLightUser === false);

  const canUseExpenses = computed(() =>
    accountSettingsStore.accountSettings.UseExpenses === true && userConfigurationStore.config.Permissions.AllowedToMakeExpenses === true
  );

  const loginDestination = computed(() => {
    if (config.public.mfaOnly || userConfigurationStore.userConfiguration?.IsWebUser) {
      return '/mfa';
    }

    if (userConfigurationStore.isLightUser) {
      return '/hourregistrations';
    }

    return '/home';
  });

  const validLightUserRoutes = [
    '/hourregistrations'
  ];

  interface RouteRule {
    priority: number;
    check: (path: string) => 'continue' | 'invalid' | 'valid';
  }

  const routeRules: RouteRule[] = [
    {
      priority: 0,
      check: (path: string) => path === '/' ? 'valid' : 'continue'
    },
    {
      priority: 0,
      check: (path: string) => {
        if (userConfigurationStore.userConfiguration?.IsWebUser) {
          return path === '/mfa' ? 'valid' : 'invalid';
        }

        return 'continue';
      }
    },
    {
      priority: 0,
      check: (path: string) => {
        if (path === '/mfa') {
          return config.public.mfaOnly === true || userConfigurationStore.userConfiguration?.IsWebUser ? 'valid' : 'invalid';
        } else {
          return 'continue';
        }
      }
    },
    {
      priority: 0,
      check: (path: string) => {
        if (path.includes('test')) {
          return config.public.showDevPages === true ? 'valid' : 'invalid';
        } else {
          return 'continue';
        }
      }
    },
    {
      priority: 10,
      check: (path: string) => {
        if (userConfigurationStore.isLightUser) {
          return validLightUserRoutes.includes(path) ? 'valid' : 'invalid';
        } else {
          return 'continue';
        }
      }
    },
    {
      priority: 10,
      check: (path: string) => {
        if (path === '/expenses') {
          return canUseExpenses.value ? 'valid' : 'invalid';
        } else {
          return 'continue';
        }
      }
    }
  ];

  const sortedRouteRules = routeRules.sort((a, b) => a.priority - b.priority);

  // This could probably be improved by using glob pattern matching or something similar. Minimatch seems to be a good package for this.
  function isRoutePermitted(path: string) {
    const lowercasePath = path.toLowerCase();

    for (const rule of sortedRouteRules) {
      switch (rule.check(lowercasePath)) {
        case 'valid':
          return true;

        case 'invalid':
          return false;

        case 'continue':
          break;
      }
    }

    return true;
  }

  return {
    hasMFA,
    loginDestination,
    hasMessages,
    hasShowAllTab,
    hasShowResponsibleTab,
    canUseExpenses,
    isRoutePermitted
  };
});
